/**
 * Restorable editor settings per spread (e.g. Preferences → Nudge).
 *
 * GET /checkpoints/{spreadId}/editorsettings/restorablesettings
 * PUT /checkpoints/{spreadId}/editorsettings/restorablesettings/update
 */

import { httpClient } from '@/api/http/client'
import { logger } from '@/utils/logger'

/** Matches Preferences dialog Nudge field (pt). */
const NUDGE_MIN = 1
const NUDGE_DEFAULT = 1

export const NUDGE_MAX_PT = 100

function enc(value: string): string {
  return encodeURIComponent(value.trim())
}

function restorableSettingsPath(spreadId: string): string {
  return `/checkpoints/${enc(spreadId)}/editorsettings/restorablesettings`
}

function restorableUpdatePath(spreadId: string): string {
  return `/checkpoints/${enc(spreadId)}/editorsettings/restorablesettings/update`
}

export interface RestorableSettingsPutBody {
  nudge: number
}

/** Values for Preferences UI */
export interface RestorableSettingsLoaded {
  /** String for numeric input, e.g. `"2"` */
  nudge: string
}

function parseNudgeToPt(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return clampNudgePt(Math.round(raw))
  }

  if (typeof raw === 'string') {
    const t = raw.trim()

    if (t === '') return NUDGE_DEFAULT
    const n = parseInt(t, 10)

    if (!Number.isFinite(n)) return NUDGE_DEFAULT

    return clampNudgePt(n)
  }

  return NUDGE_DEFAULT
}

export function clampNudgePt(n: number): number {
  if (!Number.isFinite(n)) return NUDGE_DEFAULT
  const rounded = Math.round(n)

  return Math.min(NUDGE_MAX_PT, Math.max(NUDGE_MIN, rounded))
}

function ptToPreferenceString(pt: number): string {
  return String(clampNudgePt(pt))
}

async function getRestorableSettings(spreadId: string): Promise<RestorableSettingsLoaded | null> {
  try {
    const dto = await httpClient.get<{ nudge?: number | string }>(restorableSettingsPath(spreadId))

    if (dto == null || typeof dto !== 'object') return null

    const nudgePt = parseNudgeToPt(dto.nudge)

    return {
      nudge: ptToPreferenceString(nudgePt),
    }
  } catch (error) {
    logger.warn(`Failed to load restorable settings for spread ${spreadId}:`, error)
    return null
  }
}

async function updateRestorableSettings(
  spreadId: string,
  body: RestorableSettingsPutBody
): Promise<void> {
  const payload: RestorableSettingsPutBody = {
    nudge: clampNudgePt(body.nudge),
  }

  await httpClient.put<RestorableSettingsPutBody, unknown>(restorableUpdatePath(spreadId), payload)
}

export const restorableSettingsService = {
  getRestorableSettings,
  updateRestorableSettings,
}
